import { useEffect, useMemo, useState } from "react";
import { Modal, InputSwitch, InputTextCustom } from "medere-ui-framework";
import { Button } from "primereact/button";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import CustomCheckBox from "../../../../components/CustomCheckBox/CustomCheckBox";
import KParameterInput from "./components/kparameter/KParameterInput";
import { Sector, QueueNumerator, Totem, AssociatedBox, TotemForm, defaultTotemForm, defaultSector, Establishment, defaultEstablishment, defaultQueueNumerator } from "../../../../interface/interface";
import { QueueNumeratorTable } from "./components/QueueNumeratorTable";
import { AssociatedBoxTable } from "../patientcallertab/components/AssociatedBoxTable";
import useResizeListener from "../../../../hooks/useResizeListener";
import { findPreferredEstablishment } from "../../preferredEstablishment";

interface Props {
    visible: boolean;
    setVisible: (v: boolean) => void;
    onSubmit: (newTotem: Totem) => void;
    totemToEdit?: Totem;
}

const secretaryQueueNumerator: QueueNumerator = {
    queuenumerator: null,
    queuestringid: 'S',
    optiondescription: 'Secretaría',
    _order: 30,
    lastnumber: null,
    active: true,
    medicalscheduletemplate: null,
    medicalscheduletemplatedesc: '',
};

export default function CRUDTotemModal({ visible, setVisible, onSubmit, totemToEdit }: Props) {
    const isMobile = useResizeListener(768);
    const { establishments, activeEstablishments, preferredBuildingId } = useLocationConfigContext();

    const [form, setForm] = useState<TotemForm>(() => initForm());
    const [formErrors, setFormErrors] = useState<{ name?: string; description?: string; totemId?: string; printerURL?: string; waitingQueueDesc?: string; confirmAssistDesc?: string; appointmentDesc?: string; secretaryAttendanceDesc?: string; types?: string; waitingQueueItems?: string }>({})
    const [secretaryQN, setSecretaryQN] = useState<QueueNumerator | null>(null)

    // Síncrono (no en un efecto): AssociatedBoxTable lo toma al montarse. En el alta arranca con el
    // establecimiento preferido del usuario, si está activo.
    const preSelectedEstablishment = useMemo<Establishment | null>(() => {
        if (totemToEdit) {
            return totemToEdit.locationdtolist?.length > 0 ? establishments.find(e => e.buildingid === totemToEdit.establishment) ?? null : null;
        }
        return findPreferredEstablishment(activeEstablishments, preferredBuildingId);
    }, [totemToEdit, establishments, activeEstablishments, preferredBuildingId]);

    const [queueNumeratorListToRemove, setQueueNumeratorListToRemove] = useState<number[]>([]);
    const mobileYesNoLabelStyle = isMobile ? { marginTop: "0.5rem", display: "inline-block" } : undefined;
    const totemIdFieldStyle = isMobile ? { width: "25%", minWidth: "5.5rem", maxWidth: "7.5rem" } : undefined;

    useEffect(() => {
        if (!visible) return;
        setForm(initForm());
    }, [visible, totemToEdit]);

    useEffect(() => {
        if (!form.secretaryAttendance) return;

        setForm((prevForm) => {
            const updatedWaitingQueue = prevForm.waitingQueue.map((item) =>
                item.queuestringid === 'S' ? { ...item, optiondescription: prevForm.secretaryAttendanceDesc || 'Secretaría' } : item
            );
            return { ...prevForm, waitingQueue: updatedWaitingQueue };
        });
    }, [form.secretaryAttendanceDesc]);

    useEffect(() => {
        if (secretaryQN)
            form
    }, [secretaryQN])

    function initForm(): TotemForm { //Se encarga de precargar el totem a editar o inicializar el totem a crear
        if (!totemToEdit) {
            return defaultTotemForm
        }
        const hwq = (totemToEdit as any).hasWaitingQueue ?? (totemToEdit as any).haswaitingqueue ?? false;

        const establishment = establishments?.find(est => est.buildingid === totemToEdit.establishment) ?? defaultEstablishment;
        return {
            totem: totemToEdit.totem,
            name: totemToEdit.description, //Para el Input "Nombre"
            totemId: totemToEdit.totemid ? String(totemToEdit.totemid) : '',
            description: totemToEdit.totemapptitle, //Para el Input "Título para pantalla"
            printerEnabled: totemToEdit.printerenabled,
            printerURL: totemToEdit.printerenabled ? totemToEdit.printerurl : "",
            appointments: totemToEdit.appointments,
            appointmentDesc: totemToEdit.appointmentdescription,
            waitingQueue: (hwq || totemToEdit.secretaryattendance) ? totemToEdit.queuenumeratordtolist : [],
            kparameter: (hwq && totemToEdit.kparameter) ? String(totemToEdit.kparameter) : "",
            hasWaitingQueue: hwq,
            waitingQueueDesc: totemToEdit.queuenumeratordescription,
            confirmAssist: totemToEdit.defaultconfirmattend,
            confirmAssistDesc: totemToEdit.confirmassitancedescription,
            patientRegistration: hwq ? totemToEdit.patientregistration : false,
            secretaryAttendance: totemToEdit.secretaryattendance,
            secretaryAttendanceDesc: totemToEdit.secretaryattendancedescription,
            active: totemToEdit.active,
            associatedBoxes: totemToEdit.locationdtolist?.map((b) => ({ establishment: establishment, sector: { ...defaultSector, sector: b.sector, name: b.sectorstring }, box: b }))
        };
    }

    const updateAssociatedBoxes = (updatedAssociatedBoxes: AssociatedBox[]) => { //Actualiza los mediacontent segun se hayan creado, editado (activo o datos) o eliminado
        setForm((t) => ({ ...t, associatedBoxes: updatedAssociatedBoxes }));
    };

    const updateQueueNumerators = (updatedQueueNumerators: QueueNumerator[]) => {
        const unique = updatedQueueNumerators.filter((item, index, self) =>
            index === self.findIndex(t => t.queuestringid === item.queuestringid)
        );
        setForm((t) => ({ ...t, waitingQueue: unique }));
        setFormErrors((prev) => ({ ...prev, waitingQueueItems: "" }));
    };

    const removeWaitingQueue = (qn: QueueNumerator, idx: number, force = false) => {
        if (qn.queuestringid === 'S' && !force) return; // bloquea acciones de la tabla
        setForm(t => {
            if (qn.queuenumerator) {
                setQueueNumeratorListToRemove(prev => [...prev, qn.queuenumerator!]);
            }
            return { ...t, waitingQueue: t.waitingQueue.filter((_, i) => i !== idx) };
        });
    };

    const handleToggleSecretaryAttendance = (checked: boolean) => {
        if (checked) {
            if (!form.waitingQueue.some(x => x.queuestringid === 'S')) {
                updateQueueNumerators([...form.waitingQueue, { ...secretaryQueueNumerator, optiondescription: form.secretaryAttendanceDesc || 'Secretaría' }]);
            }
            return;
        }
        const i = form.waitingQueue.findIndex(x => x.queuestringid === 'S');
        if (i !== -1) removeWaitingQueue(form.waitingQueue[i], i, true); // force
    };

    const handleSubmit = () => { //Forma el totem a guardar y lo manda al handleSave de TotemView
        const errors = validateForm(form);
        if (!form.hasWaitingQueue && !form.confirmAssist && !form.appointments && !form.secretaryAttendance) {
            errors.types = "Debe marcar al menos un tipo";
        }
        if (form.hasWaitingQueue && (!form.waitingQueue || form.waitingQueue.length <= 0)) {
            errors.waitingQueueItems = "Debe agregar al menos un elemento al contenido de pantalla";
        }
        setFormErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;
        if (hasErrors) {
            return
        }

        const filteredQueue = form.waitingQueue.filter(qn => {
            if (form.hasWaitingQueue) return true;
            if (form.secretaryAttendance && qn.queuestringid === 'S') return true;
            return false;
        });


        const newTotem: Totem = { //meto el contenido cargado en el formulario en un totem auxiliar, para pasarlo al handleSave en TotemView y guardarlo en el original
            totem: form.totem,
            totemapptitle: form.description, //Input "Titulo para pantalla"
            totemid: Number(form.totemId),
            description: form.name, //Input "Nombre"
            printerenabled: form.printerEnabled,
            printerurl: form.printerEnabled ? form.printerURL : null,
            appointments: form.appointments,
            appointmentdescription: form.appointments ? form.appointmentDesc : '',
            queuenumeratordtolist: filteredQueue.length > 0 ? filteredQueue : null,
            kparameter: form.hasWaitingQueue ? Number(form.kparameter) : 0,
            hasWaitingQueue: form.hasWaitingQueue,
            queuenumeratordescription: form.hasWaitingQueue ? form.waitingQueueDesc : '',
            defaultconfirmattend: form.confirmAssist,
            confirmassitancedescription: form.confirmAssist ? form.confirmAssistDesc : '',
            patientregistration: form.hasWaitingQueue ? form.patientRegistration : false,
            secretaryattendance: form.secretaryAttendance,
            secretaryattendancedescription: form.secretaryAttendanceDesc,
            active: form.active,
            establishment: totemToEdit ? totemToEdit.establishment : 0,
            locationdtolist: (form.hasWaitingQueue || form.secretaryAttendance) ? form.associatedBoxes.map((c) => c.box) : [],
            queuenumeratordtolisttoremove: queueNumeratorListToRemove?.length > 0 ? queueNumeratorListToRemove : null
        };

        onSubmit(newTotem)
    };

    const handleToggleWaitingQueue = (checked: boolean) => {
        setFormErrors((prev) => ({ ...prev, types: "", waitingQueueItems: "", waitingQueueDesc: "" }));
        setForm(prev => {
            if (checked) return { ...prev, hasWaitingQueue: true };

            const toRemove = (prev.waitingQueue || [])
                .filter(qn => qn.queuenumerator && (qn.queuestringid !== 'S' || !prev.secretaryAttendance))
                .map(qn => qn.queuenumerator as number);

            setQueueNumeratorListToRemove(curr => [...curr, ...toRemove]);

            const keepS = prev.secretaryAttendance
                ? prev.waitingQueue.filter(qn => qn.queuestringid === 'S')
                : [];

        return {
        ...prev,
        hasWaitingQueue: false,
        waitingQueueDesc: '',
        kparameter: '',
        patientRegistration: false,
        waitingQueue: keepS,
        associatedBoxes: prev.secretaryAttendance ? prev.associatedBoxes : [],
        };
    });
    };
    const asBool = (v: any) => typeof v === 'boolean' ? v : !!(v?.target?.checked ?? v?.checked ?? v?.value);

    const validateForm = (form: TotemForm): { name?: string; description?: string; totemId?: string; printerURL?: string; waitingQueueDesc?: string; confirmAssistDesc?: string; appointmentDesc?: string; secretaryAttendanceDesc?: string; types?: string; waitingQueueItems?: string } => {
        const errors: { name?: string; description?: string; totemId?: string; printerURL?: string; waitingQueueDesc?: string; confirmAssistDesc?: string; appointmentDesc?: string; secretaryAttendanceDesc?: string; types?: string; waitingQueueItems?: string } = {};
        if (!form.name.trim()) errors.name = "Este campo es requerido";
        if (!form.description.trim()) errors.description = "Este campo es requerido";
        if (!form.totemId.trim()) errors.totemId = "Este campo es requerido";
        if (form.printerEnabled && !form.printerURL.trim()) errors.printerURL = "Este campo es requerido si activa impresora";
        if (form.hasWaitingQueue && !form.waitingQueueDesc?.trim()) errors.waitingQueueDesc = "Este campo es requerido si marca cola de espera";
        if (form.confirmAssist && !form.confirmAssistDesc?.trim()) errors.confirmAssistDesc = "Este campo es requerido si marca confirmación de asistencia";
        if (form.appointments && !form.appointmentDesc?.trim()) errors.appointmentDesc = "Este campo es requerido si marca turnos";

        return errors;
    };

    return (
        <Modal className="location-mobile-editor-modal location-totem-editor-modal" visible={visible} setVisible={setVisible} width="w-xlarge" header={<div><span style={{ fontWeight: 'bold', fontSize: '20px' }}>{totemToEdit ? "Editar" : "Crear"}</span><span style={{ fontWeight: 'normal', fontSize: '20px' }}> Tótem</span></div>}>
            <div className="location-config-view location-totem-modal">
                <div className="justify-self-end flex flex-row align-items-center gap-2 location-totem-modal__active-row location-switch-inline-row location-modal-active-row">
                    <InputSwitch id="inputswitchactiveid" checked={form.active} onChange={(e) => setForm({ ...form, active: e.value })} />
                    <span className="text-sm location-switch-state-label">{form.active ? "Activo" : "Inactivo"}</span>
                </div>

                <div className="flex gap-2 location-totem-modal__primary-fields">
                    <div className="w-full">
                        <p>Nombre *</p>
                        <InputTextCustom key={"inputtotemnameid"} className={formErrors.name ? "" : "border2"} value={form.name} onChange={(v) => { setForm({ ...form, name: v.target.value }); setFormErrors({ ...formErrors, name: "" }) }} error={!!formErrors.name} caption={formErrors.name} />
                    </div>
                    {<div className="w-full">
                        <p>Título para pantalla *</p>
                        <InputTextCustom key={"inputtotemtitleid"} className={formErrors.description ? "" : "border2"} value={form.description} onChange={(v) => { setForm({ ...form, description: v.target.value }); setFormErrors({ ...formErrors, description: "" }) }} error={!!formErrors.description} caption={formErrors.description} />
                    </div>}
                    <div className="location-totem-modal__id-field" style={totemIdFieldStyle}>
                        <p>ID *</p>
                        <InputTextCustom key={"inputtotemIDid"} className={formErrors.totemId ? "" : "border2"} value={form.totemId} onChange={(v) => { setForm({ ...form, totemId: v.target.value }); setFormErrors({ ...formErrors, totemId: "" }) }} error={!!formErrors.totemId} caption={formErrors.totemId} />
                    </div>
                </div>

                <div className="flex gap-4 mt-2 location-totem-modal__printer-row">
                    <div>
                        <p>¿Imprime?</p>
                        <div className="justify-self-start flex flex-row align-items-start gap-2 mt-1.5 location-switch-inline-row location-totem-switch-row">
                            <InputSwitch id="inputswitchprinterid" checked={form.printerEnabled} onChange={(e) => { setForm({ ...form, printerEnabled: e.value }); setFormErrors({ ...formErrors, printerURL: "" }) }} />
                            <span className="text-sm location-totem-mobile-switch-label" style={mobileYesNoLabelStyle}>{form.printerEnabled ? "SI" : "NO"}</span>
                        </div>
                    </div>
                    <div style={{ maxWidth: "350px" }} className="w-full">
                        <p>Url impresora {form.printerEnabled ? "*" : ""}</p>
                        <InputTextCustom key={"inputprinterurlid"} className={(!formErrors.printerURL && form.printerEnabled) ? "border2" : ""} value={form.printerURL} onChange={(v) => { setForm({ ...form, printerURL: v.target.value }); setFormErrors({ ...formErrors, printerURL: "" }) }} error={!!formErrors.printerURL} caption={formErrors.printerURL} disable={!form.printerEnabled} />
                    </div>
                </div>

                <h1 className="subtitle mt-2 mb-2.5 font-semibold">Tipo *</h1>
                {!!formErrors.types && <p className="caption-invalid mb-2">{formErrors.types}</p>}
                <div className="flex flex-column max-w-500 gap-2.5">
                    {[{
                        label: "Cola de espera",
                        checked: form.hasWaitingQueue,
                        input: form.hasWaitingQueue,
                        value: form.waitingQueueDesc ?? "",
                        error: formErrors.waitingQueueDesc,
                        onChange: (v: string) => { setForm({ ...form, waitingQueueDesc: v }); setFormErrors({ ...formErrors, waitingQueueDesc: "" }) },
                        toggle: handleToggleWaitingQueue
                    }, {
                        label: "Confirmación de asistencia",
                        checked: form.confirmAssist,
                        input: form.confirmAssist,
                        value: form.confirmAssistDesc ?? "",
                        error: formErrors.confirmAssistDesc,
                        onChange: (v: string) => { setForm({ ...form, confirmAssistDesc: v }); setFormErrors({ ...formErrors, confirmAssistDesc: "" }) },
                        toggle: (v: boolean) => {
                            setFormErrors((prev) => ({ ...prev, types: "", confirmAssistDesc: "" }));
                            setForm(prev => ({ ...prev, confirmAssist: v }));
                        },
                    }, {
                        label: "Turnos",
                        checked: form.appointments,
                        input: form.appointments,
                        value: form.appointmentDesc ?? "",
                        error: formErrors.appointmentDesc,
                        onChange: (v: string) => { setForm({ ...form, appointmentDesc: v }); setFormErrors({ ...formErrors, appointmentDesc: "" }) },
                        toggle: (v: boolean) => {
                            setFormErrors((prev) => ({ ...prev, types: "", appointmentDesc: "" }));
                            setForm(prev => ({ ...prev, appointments: v }));
                        },
                    }, {
                        label: "Atención en secretaria",
                        checked: form.secretaryAttendance,
                        input: form.secretaryAttendance,
                        value: form.secretaryAttendanceDesc ?? "",
                        error: null,
                        onChange: (v: string) => { setForm({ ...form, secretaryAttendanceDesc: v }); setFormErrors({ ...formErrors, secretaryAttendanceDesc: "" }) },
                        toggle: (v: boolean) => { setForm({ ...form, secretaryAttendance: v }); handleToggleSecretaryAttendance(v) }
                    }].map((item, idx) => (
                        <div key={idx} className="flex flex-column" style={{ gap: "0.5rem" }}>
                            <div className="flex align-items-center min-h-9" style={{ gap: "0.5rem" }}>
                                <CustomCheckBox
                                    key={"checkserviceid"}
                                    checked={item.checked}
                                    onChange={(v) => item.toggle(asBool(v))}
                                />
                                <span className="leading-none">{item.label}</span>
                            </div>
                            {item.input && (
                                <div className="w-full">
                                    <InputTextCustom
                                        key={"inputservicenameid"}
                                        className={item.error ? "w-full" : "border2 w-full"}
                                        value={item.value}
                                        onChange={(e) => item.onChange(e.target.value)}
                                        error={!!item.error}
                                        caption={item.error ?? ""}
                                        placeholder={item.checked ? "Título del servicio *" : "Título del servicio"}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {form.hasWaitingQueue ? (<div>
                    <h1 className="subtitle mt-5 font-semibold">Contenido de pantalla</h1>
                    {!!formErrors.waitingQueueItems && <p className="caption-invalid mb-2">{formErrors.waitingQueueItems}</p>}
                    <div className={isMobile ? "" : "location-totem-table-scroll"}>
                        <QueueNumeratorTable
                            queueNumerators={form.waitingQueue}
                            onChange={(newQueueNumerators) => updateQueueNumerators(newQueueNumerators)}
                            onDelete={(queueNumerator, index) => removeWaitingQueue(queueNumerator, index)}
                            modalWidth="w-xlarge"
                        />
                    </div>

                    <div className="flex gap-4 mt-4 mb-2 location-totem-modal__extra-fields">
                        <div>
                            <p>¿Registra pacientes?</p>
                            <div className="justify-self-start flex flex-row align-items-start gap-2 mt-1.5 location-switch-inline-row location-totem-switch-row">
                                <InputSwitch id="inputswitchregistrationid" checked={form.patientRegistration} onChange={(e) => setForm({ ...form, patientRegistration: e.value })} />
                                <span className="text-sm location-totem-mobile-switch-label" style={mobileYesNoLabelStyle}>{form.patientRegistration ? "SI" : "NO"}</span>
                            </div>
                        </div>
                        <KParameterInput
                            key={"inputkparameterid"}
                            value={form.kparameter}
                            onChange={(v) => setForm({ ...form, kparameter: v.target.value })}
                        />
                    </div>
                </div>):null}

                {(form.hasWaitingQueue || form.secretaryAttendance) ? (<div>
                    <h1 className="subtitle mt-2 font-semibold">Puestos asociados</h1>
                    <div className={isMobile ? "" : "location-totem-table-scroll"}>
                        <AssociatedBoxTable
                            associatedBoxes={form.associatedBoxes}
                            establishments={totemToEdit ? establishments : activeEstablishments}
                            onChange={(newAssociatedBoxes) => updateAssociatedBoxes(newAssociatedBoxes)}
                            preSelectedEstablishment={preSelectedEstablishment ?? defaultEstablishment}
                            itemToEdit={totemToEdit}
                            modalWidth="w-xlarge"
                        />
                    </div>
                </div>) : null}

                <div className="flex justify-content-end align-items-center mt-6 gap-2 location-totem-modal__footer">
                    <Button id="cancelbuttonid" label="Cancelar" onClick={() => setVisible(false)} className="btnCancel2 btnWidth" />
                    <Button id="savetotemid" label={totemToEdit ? "Guardar" : "Crear"} className="btnContinue btnWidth" onClick={handleSubmit} />
                </div>
            </div>
        </Modal>
    );
}

